import { ExtractionQualityMeter } from "@/components/ExtractionQualityMeter";
import { ReviewActions } from "@/components/ReviewActions";
import { SuggestedCorrectionsPanel } from "@/components/SuggestedCorrectionsPanel";
import { SystemRecordSection } from "@/components/SystemRecordSection";
import { TopNav } from "@/components/TopNav";
import { ValidationIcon } from "@/components/ValidationIcon";
import { useDocumentPolling, useDocumentViewUrl } from "@/hooks/useDocuments";
import { ExtractedFieldData } from "@/types/validation";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";

type Correction = {
  field: string;
  originalValue: string;
  correctedValue: string;
  confidence: number;
  reason?: string | null;
  correctedAt?: string;
};

function getConfidenceColor(confidence: number): string {
  if (confidence >= 90) return "bg-green-500";
  if (confidence >= 75) return "bg-yellow-400";
  return "bg-red-500";
}

function getConfidenceColorHighlight(confidence: number): string {
  if (confidence >= 90) return "bg-green-500/10 dark:bg-green-500/20";
  if (confidence >= 75) return "bg-yellow-400/10 dark:bg-yellow-500/20";
  return "bg-red-500/10 dark:bg-red-500/20";
}

function getConfidenceTextColor(confidence: number): string {
  if (confidence >= 90) return "text-slate-500 dark:text-slate-400";
  if (confidence >= 75) return "text-yellow-700 dark:text-yellow-400";
  return "text-red-700 dark:text-red-400";
}

function getConfidenceValueColor(confidence: number): string {
  if (confidence >= 90) return "text-slate-900 dark:text-slate-100";
  if (confidence >= 75) return "text-yellow-800 dark:text-yellow-300";
  return "text-red-800 dark:text-red-300";
}

function formatFieldName(field: string): string {
  return field
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Format value for ValidationIcon display - creates human-readable strings for objects and arrays
 */
function formatValueForValidation(
  value: unknown,
  fieldName?: string
): string | number | boolean | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  // For arrays, format based on field type
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";

    // shipment_details - format as table-like text
    if (fieldName === "shipment_details") {
      return value
        .map((item: Record<string, unknown>, index: number) => {
          const parts: string[] = [];
          parts.push(`Line ${item.seq_number || index + 1}`);
          if (item.description) parts.push(String(item.description));
          if (item.pieces !== undefined) parts.push(`${item.pieces} ${item.pieces_unit || "pcs"}`);
          if (item.weight) parts.push(`${item.weight} ${item.weight_unit || "LBS"}`);
          if (item.actual_class) parts.push(`Class ${item.actual_class}`);
          if (item.commodity_code) parts.push(`NMFC ${item.commodity_code}`);
          return parts.join(" • ");
        })
        .join(" | ");
    }

    // trace_numbers - format as list
    if (fieldName === "trace_numbers") {
      return value
        .map((item: Record<string, unknown>) => {
          const type = item.description || item.type_description || item.type;
          return `${type}: ${item.value}`;
        })
        .join(" | ");
    }

    // handwritten_notes - format as list
    if (fieldName === "handwritten_notes" || fieldName === "suggested_notes") {
      return value
        .map((item, index) => {
          const content =
            typeof item === "string"
              ? item
              : (item as Record<string, unknown>).content || (item as Record<string, unknown>).note;
          return `${index + 1}. ${content}`;
        })
        .join(" | ");
    }

    // Simple arrays of strings/numbers
    if (value.every((item) => typeof item === "string" || typeof item === "number")) {
      return value.map(String).join(", ");
    }

    // Other arrays
    return `${value.length} items`;
  }

  // For shipping entities, format as readable text
  if (typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;

    if (fieldName === "shipper" || fieldName === "consignee" || fieldName === "bill_to") {
      const parts: string[] = [];
      if (obj.name) parts.push(String(obj.name));
      if (obj.address) parts.push(String(obj.address));
      const cityStateZip = [obj.city, obj.state, obj.zip_code]
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .map(String)
        .join(", ");
      if (cityStateZip) parts.push(cityStateZip);
      if (obj.contact_name) parts.push(`Contact: ${String(obj.contact_name)}`);
      if (obj.contact_phone) parts.push(`Phone: ${String(obj.contact_phone)}`);
      if (obj.ref_client_code) parts.push(`Ref: ${String(obj.ref_client_code)}`);
      return parts.join(" | ");
    }
  }

  // Fallback to JSON for other complex types
  return JSON.stringify(value);
}

/**
 * Format field value for display - handles arrays, objects, and primitives
 */
function formatFieldValue(value: unknown, fieldName?: string): React.ReactNode {
  // Null or undefined
  if (value === null || value === undefined) {
    return <span className="text-slate-400 dark:text-slate-500 italic">N/A</span>;
  }

  // Boolean
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  // Number
  if (typeof value === "number") {
    return value.toString();
  }

  // String
  if (typeof value === "string") {
    return value;
  }

  // Array
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-slate-400 dark:text-slate-500 italic">None</span>;
    }

    // Special handling for shipment_details - render as a detailed table
    if (fieldName === "shipment_details") {
      return (
        <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-xs">
            <thead className="bg-slate-100 dark:bg-slate-800">
              <tr>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Line
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Qty
                </th>
                <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Weight
                </th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Commodity Code
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
              {value.map((item: Record<string, unknown>, index: number) => (
                <tr
                  key={index}
                  className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <td className="px-3 py-2.5 whitespace-nowrap text-center text-slate-900 dark:text-white font-medium">
                    {String(item.seq_number || index + 1)}
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300">
                    <div className="max-w-xs">
                      {item.description !== undefined && item.description !== null
                        ? String(item.description)
                        : "—"}
                    </div>
                    {typeof item.said_to_contain === "string" &&
                      item.said_to_contain.trim() !== "" && (
                        <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 italic">
                          Contains: {item.said_to_contain}
                        </div>
                      )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right text-slate-900 dark:text-white">
                    {item.pieces !== undefined && item.pieces !== null ? (
                      <div className="space-y-0.5">
                        <div>
                          {String(item.pieces)}{" "}
                          {item.pieces_unit !== undefined &&
                            item.pieces_unit !== null &&
                            String(item.pieces_unit).trim() !== "" && (
                              <span className="text-slate-500 dark:text-slate-400 text-[10px]">
                                {String(item.pieces_unit)}
                              </span>
                            )}
                        </div>
                        {item.pallets !== undefined && item.pallets !== null && (
                          <div className="text-[10px] text-slate-500 dark:text-slate-400">
                            {String(item.pallets)}{" "}
                            {item.pallets_unit !== undefined && item.pallets_unit !== null
                              ? String(item.pallets_unit)
                              : "PLT"}
                          </div>
                        )}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-right text-slate-900 dark:text-white tabular-nums">
                    {item.weight !== undefined && item.weight !== null ? (
                      <>
                        {String(item.weight)}{" "}
                        <span className="text-slate-500 dark:text-slate-400 text-[10px]">
                          {String(item.weight_unit || "LBS")}
                        </span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-center text-slate-900 dark:text-white font-medium">
                    {item.actual_class !== undefined && item.actual_class !== null
                      ? String(item.actual_class)
                      : "—"}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-center text-slate-900 dark:text-white">
                    {item.commodity_code !== undefined && item.commodity_code !== null
                      ? String(item.commodity_code)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Special handling for trace_numbers - render as a compact table
    if (fieldName === "trace_numbers") {
      return (
        <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
            <thead className="bg-slate-100 dark:bg-slate-800">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
              {value.map((item: Record<string, unknown>, index: number) => {
                const hasContent = item.description || item.type_description || item.value;
                if (!hasContent) return null;

                return (
                  <tr
                    key={index}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap font-medium">
                      {String(item.description || item.type_description || item.type)}
                    </td>
                    <td className="px-4 py-2.5 text-xs font-medium text-slate-900 dark:text-white">
                      {String(item.value)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    // Special handling for handwritten_notes and suggested_notes - render as numbered list
    if (
      fieldName === "handwritten_notes" ||
      fieldName === "suggested_notes" ||
      fieldName === "suggested_instruction_codes"
    ) {
      // Check if it's an array of strings
      if (value.every((item) => typeof item === "string")) {
        return (
          <ol className="mt-2 space-y-1.5 list-decimal list-inside text-xs">
            {value.map((item, index) => (
              <li key={index} className="text-slate-700 dark:text-slate-300 leading-relaxed">
                {String(item)}
              </li>
            ))}
          </ol>
        );
      }

      // Handle handwritten_notes with object format {content, location, confidence}
      if (fieldName === "handwritten_notes") {
        return (
          <div className="mt-2 space-y-2">
            {value.map((item: Record<string, unknown>, index: number) => {
              const content =
                typeof item === "string" ? item : item.content || item.note || item.text;
              if (!content) return null;

              return (
                <div
                  key={index}
                  className="bg-slate-50 dark:bg-slate-800 rounded px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-700"
                >
                  <div className="text-slate-700 dark:text-slate-300">{String(content)}</div>
                  {typeof item === "object" &&
                    item !== null &&
                    typeof item.location === "string" &&
                    item.location.trim() !== "" && (
                      <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Location: {item.location}
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        );
      }
    }

    // Arrays of strings or simple values - show as inline badges (for tags, etc.)
    if (value.every((item) => typeof item === "string" || typeof item === "number")) {
      // For empty arrays or long lists, handle specially
      if (value.length === 0) {
        return <span className="text-slate-400 dark:text-slate-500 italic text-xs">None</span>;
      }

      // For short lists (≤3 items), show inline
      if (value.length <= 3) {
        return (
          <div className="flex flex-wrap gap-1 mt-1">
            {value.map((item, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
              >
                {String(item)}
              </span>
            ))}
          </div>
        );
      }

      // For longer lists, show as vertical list
      return (
        <ul className="mt-2 space-y-1 text-xs">
          {value.map((item, index) => (
            <li key={index} className="text-slate-700 dark:text-slate-300">
              • {String(item)}
            </li>
          ))}
        </ul>
      );
    }

    // Complex arrays - show count
    return (
      <span className="text-slate-600 dark:text-slate-400">
        {value.length} item{value.length !== 1 ? "s" : ""}
      </span>
    );
  }

  // Object (like shipper, consignee, bill_to)
  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;

    // Special handling for shipping entities (shipper, consignee, bill_to)
    if (fieldName === "shipper" || fieldName === "consignee" || fieldName === "bill_to") {
      return (
        <div className="mt-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
          {obj.name !== undefined && obj.name !== null && String(obj.name).trim() !== "" && (
            <div className="font-semibold text-slate-900 dark:text-white mb-2">
              {String(obj.name)}
            </div>
          )}
          <div className="space-y-1 text-xs">
            {obj.address !== undefined &&
              obj.address !== null &&
              String(obj.address).trim() !== "" && (
                <div className="text-slate-700 dark:text-slate-300">📍 {String(obj.address)}</div>
              )}
            {((obj.city !== undefined && obj.city !== null && String(obj.city).trim() !== "") ||
              (obj.state !== undefined && obj.state !== null && String(obj.state).trim() !== "") ||
              (obj.zip_code !== undefined &&
                obj.zip_code !== null &&
                String(obj.zip_code).trim() !== "")) && (
              <div className="text-slate-700 dark:text-slate-300">
                {[obj.city, obj.state, obj.zip_code]
                  .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
                  .map(String)
                  .join(", ")}
              </div>
            )}
            {obj.contact_name !== undefined &&
              obj.contact_name !== null &&
              String(obj.contact_name).trim() !== "" && (
                <div className="text-slate-600 dark:text-slate-400">
                  👤 {String(obj.contact_name)}
                </div>
              )}
            {obj.contact_phone !== undefined &&
              obj.contact_phone !== null &&
              String(obj.contact_phone).trim() !== "" && (
                <div className="text-slate-600 dark:text-slate-400">
                  📞 {String(obj.contact_phone)}
                  {obj.contact_phone_ext !== undefined &&
                    obj.contact_phone_ext !== null &&
                    String(obj.contact_phone_ext).trim() !== "" &&
                    ` ext. ${String(obj.contact_phone_ext)}`}
                </div>
              )}
            {obj.contact_email !== undefined &&
              obj.contact_email !== null &&
              String(obj.contact_email).trim() !== "" && (
                <div className="text-slate-600 dark:text-slate-400">
                  ✉️ {String(obj.contact_email)}
                </div>
              )}
            {obj.ref_client_code !== undefined &&
              obj.ref_client_code !== null &&
              String(obj.ref_client_code).trim() !== "" && (
                <div className="text-slate-500 dark:text-slate-500 text-[10px] mt-1">
                  Ref: {String(obj.ref_client_code)}
                </div>
              )}
          </div>
        </div>
      );
    }

    // Generic object rendering for other object types
    return (
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-[320px] text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900">
          <tbody>
            {Object.entries(obj).map(([k, v]) =>
              v !== null && v !== undefined && String(v).trim() !== "" ? (
                <tr key={k}>
                  <td className="px-2 py-1 font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap capitalize">
                    {formatFieldName(k)}
                  </td>
                  <td className="px-2 py-1 text-slate-900 dark:text-white whitespace-nowrap">
                    {String(v)}
                  </td>
                </tr>
              ) : null
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback to string
  return String(value);
}

export default function DocumentViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch document from API - with polling for processing status
  const {
    data: documentData,
    isLoading,
    error,
  } = useDocumentPolling(
    id,
    true // Enable polling
  );

  // Fetch presigned URL for viewing the document
  const { data: viewUrlData, isLoading: isLoadingUrl, error: urlError } = useDocumentViewUrl(id);

  const handleGoBack = () => {
    // Use navigate(-1) to preserve URL params (pagination, sorting, filters)
    navigate(-1);
  };

  const handleProcessNew = () => {
    // Reset to fresh dashboard state
    navigate("/dashboard");
  };

  const handleFlagForReview = () => {
    // Will be implemented with API integration
    enqueueSnackbar(`Flagging document ${id} for review`, { variant: "info" });
  };

  const handleExportJSON = () => {
    if (!documentData) return;
    const dataStr = JSON.stringify(documentData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = `${documentData.fileName}_extraction.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Function to render document preview based on content type
  const renderDocumentPreview = () => {
    if (isLoadingUrl) {
      return (
        <div className="text-center p-8">
          <svg
            className="animate-spin h-12 w-12 text-primary mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-gray-600 dark:text-gray-400">Loading preview...</p>
        </div>
      );
    }

    if (urlError || !viewUrlData) {
      return (
        <div className="text-center p-8">
          <svg
            className="h-24 w-24 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-600 dark:text-gray-400 text-lg font-medium mb-2">
            {documentData?.fileName}
          </p>
          <p className="text-gray-500 dark:text-gray-500 text-sm">Document preview not available</p>
          {urlError && (
            <p className="text-red-500 dark:text-red-400 text-xs mt-2">
              Error: {urlError instanceof Error ? urlError.message : "Failed to load preview"}
            </p>
          )}
        </div>
      );
    }

    const contentType = viewUrlData.content_type.toLowerCase();
    const downloadUrl = viewUrlData.download_url;
    const fileName = documentData?.fileName || "";

    // Debug logging
    // eslint-disable-next-line no-console
    console.log("Document preview:", { contentType, fileName, downloadUrl });

    // Handle PDF files
    if (contentType === "application/pdf" || fileName.toLowerCase().endsWith(".pdf")) {
      return (
        <iframe
          src={downloadUrl}
          className="w-full h-full"
          title={`PDF: ${documentData?.fileName}`}
        />
      );
    }

    // Handle TIFF files specially - browsers don't natively support TIFF
    if (contentType === "image/tiff" || contentType === "image/tif" || /\.tiff?$/i.test(fileName)) {
      return (
        <div className="text-center p-8">
          <svg
            className="h-24 w-24 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-600 dark:text-gray-400 text-lg font-medium mb-2">
            {documentData?.fileName}
          </p>
          <p className="text-gray-500 dark:text-gray-500 text-sm mb-2">
            TIFF/TIF files cannot be previewed in the browser
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-xs mb-4">
            Please download the file to view it with an image viewer
          </p>
          <a
            href={downloadUrl}
            download={documentData?.fileName}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Download TIFF File
          </a>
        </div>
      );
    }

    // Handle other image files (JPEG, JPG, PNG, GIF, WebP)
    if (contentType.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName)) {
      return (
        <img
          className="max-h-full max-w-full object-contain p-4 lg:p-8"
          src={downloadUrl}
          alt={`Document: ${documentData?.fileName}`}
        />
      );
    }

    // Handle text files
    if (contentType.startsWith("text/") || /\.txt$/i.test(fileName)) {
      return (
        <iframe
          src={downloadUrl}
          className="w-full h-full bg-white dark:bg-slate-800"
          title={`Text: ${documentData?.fileName}`}
        />
      );
    }

    // For other file types, show a download message
    return (
      <div className="text-center p-8">
        <svg
          className="h-24 w-24 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-gray-600 dark:text-gray-400 text-lg font-medium mb-2">
          {documentData?.fileName}
        </p>
        <p className="text-gray-500 dark:text-gray-500 text-sm mb-4">
          Preview not available for this file type ({contentType})
        </p>
        <a
          href={downloadUrl}
          download={documentData?.fileName}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Download File
        </a>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-screen w-full flex-col">
        <TopNav />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <svg
              className="animate-spin h-12 w-12 text-primary mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-gray-600 dark:text-gray-400">Loading document...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-screen w-full flex-col">
        <TopNav />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center max-w-md">
            <svg
              className="h-12 w-12 text-red-500 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Failed to Load Document
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error instanceof Error ? error.message : "An error occurred"}
            </p>
            <button
              onClick={handleGoBack}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Document not found
  if (!documentData) {
    return (
      <div className="flex h-screen w-full flex-col">
        <TopNav />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center max-w-md">
            <svg
              className="h-12 w-12 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Document Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              The document you&apos;re looking for doesn&apos;t exist.
            </p>
            <button
              onClick={handleGoBack}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col">
      <TopNav />

      {/* Document Navigation */}
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-6 py-3">
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-primary dark:hover:text-primary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Dashboard
        </button>
        <button
          onClick={handleProcessNew}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Process New Document
        </button>
      </div>

      {/* Main Content */}
      <main className="grid flex-1 grid-cols-10 overflow-hidden">
        {/* Left Panel: Document Viewer */}
        <div className="col-span-10 lg:col-span-6 flex flex-col bg-slate-100 dark:bg-black/20">
          <div className="flex h-full items-center justify-center">{renderDocumentPreview()}</div>
        </div>

        {/* Right Panel: Extraction Summary */}
        <div className="col-span-10 lg:col-span-4 flex flex-col overflow-y-auto border-l border-slate-200/10 relative z-10">
          <div className="p-6">
            {/* PageHeading */}
            <div className="flex flex-wrap justify-between gap-3">
              <div className="flex min-w-72 flex-col gap-2">
                <h2 className="text-xl font-bold leading-tight text-slate-900 dark:text-white">
                  Extraction Summary
                </h2>
                <p className="text-sm font-normal leading-normal text-slate-500 dark:text-slate-400">
                  {documentData.fileName}
                </p>
              </div>
            </div>

            {/* Status Badges */}
            <div className="flex flex-wrap gap-2 py-4">
              <div className="flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                <span className="material-symbols-outlined text-base">description</span>
                {documentData.documentType ? formatFieldName(documentData.documentType) : "Unknown"}
              </div>
              <div className="flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                <span className="material-symbols-outlined text-base">business_center</span>
                Entity: {documentData.entityName || "N/A"}
              </div>
              {documentData.validationMetadata?.requires_manual_review && (
                <div className="flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                  <span
                    className="material-symbols-outlined text-base"
                    style={{ fontVariationSettings: "'FILL' 1" }}
                  >
                    error
                  </span>
                  Review Required
                </div>
              )}
            </div>

            {/* Extraction Quality Meter */}
            <ExtractionQualityMeter
              confidence={documentData.confidence || 0}
              fieldCount={
                documentData.extractedData
                  ? Object.keys(documentData.extractedData).length
                  : undefined
              }
              flaggedFieldCount={documentData.validationMetadata?.suggested_corrections?.length}
            />

            {/* Changes & Corrections Card */}
            {documentData.corrections && documentData.corrections.length > 0 && (
              <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                <div className="flex items-center gap-2 mb-3">
                  <svg
                    className="w-5 h-5 text-blue-600 dark:text-blue-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <h3 className="text-base font-bold leading-tight tracking-[-0.015em] text-blue-900 dark:text-blue-100">
                    Field Corrections Applied ({documentData.corrections.length})
                  </h3>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300 mb-4">
                  The following fields were automatically corrected with high confidence
                </p>
                <div className="flex flex-col gap-3">
                  {documentData.corrections.map((correction: Correction, index: number) => (
                    <div
                      key={index}
                      className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-blue-200 dark:border-blue-800"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {formatFieldName(correction.field)}
                          </p>
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              <span className="font-medium">Original:</span>{" "}
                              <del className="text-red-600 dark:text-red-400">
                                {correction.originalValue}
                              </del>
                            </p>
                            <p className="text-xs text-slate-700 dark:text-slate-300">
                              <span className="font-medium">Corrected:</span>{" "}
                              <span className="text-blue-700 dark:text-blue-300 font-medium">
                                {correction.correctedValue}
                              </span>
                            </p>
                          </div>
                          {correction.reason && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 italic">
                              Reason: {correction.reason}
                            </p>
                          )}
                        </div>
                        <span
                          className={`flex size-2 rounded-full ${getConfidenceColor(100)}`}
                          title="High confidence correction"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Manual Review Banner for unmatched DRs (Task 14.3) */}
            {/* Per client feedback: FE only notifies that doc needs manual review. */}
            {/* Association functionality is available via API for future use. */}
            {documentData.documentType === "DR" && documentData.status === "review_required" && (
              <div className="mt-6 p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-400 text-2xl">
                    warning
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-yellow-900 dark:text-yellow-100 uppercase tracking-tight">
                      Pending Manual Review
                    </h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      This Delivery Receipt could not be automatically associated with a shipment
                      and requires manual review.
                    </p>
                    {/* Show PRO number if available for reference */}
                    {documentData.proNumber && (
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-2">
                        <span className="font-medium">PRO Number:</span> {documentData.proNumber}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* System Shipment Record - Only show for non-DR documents (Task 14.3) */}
            {documentData.documentType !== "DR" &&
              documentData.validationMetadata?.matched_shipment_data && (
                <SystemRecordSection
                  validationMetadata={documentData.validationMetadata}
                  shipmentId={documentData.validationMetadata.matched_shipment_data.shipment_id}
                  proNumber={
                    documentData.validationMetadata.matched_shipment_data.pro_number || undefined
                  }
                  className="mt-6"
                />
              )}

            {/* Billing Completed Status Pill - Only for non-DR documents */}
            {documentData.documentType !== "DR" &&
              documentData.validationMetadata?.matched_shipment_data?.billing_completed && (
                <div className="mt-6 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                    <span className="material-symbols-outlined text-base">task_alt</span>
                    Billing Completed
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    A biller has already reviewed this BOL
                  </span>
                </div>
              )}

            {/* Suggested Corrections Panel - Only for non-DR documents (Task 14.3) */}
            {documentData.documentType !== "DR" &&
              documentData.validationMetadata &&
              documentData.validationMetadata.suggested_corrections &&
              documentData.validationMetadata.suggested_corrections.length > 0 && (
                <div className="mt-6">
                  <SuggestedCorrectionsPanel validationMetadata={documentData.validationMetadata} />
                </div>
              )}

            {/* Raw Extraction Data - Collapsible (Hide for DR documents per Task 14.3) */}
            {documentData.documentType !== "DR" &&
              documentData.extractedData &&
              Object.keys(documentData.extractedData).length > 0 && (
                <details className="mt-6 group">
                  <summary className="flex cursor-pointer items-center justify-between list-none">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-tight">
                      Raw Extraction Data
                    </h3>
                    <div className="flex items-center gap-4">
                      <span className="text-[10px] font-bold text-primary flex items-center gap-1 uppercase">
                        <span className="material-symbols-outlined text-sm">visibility</span>
                        Show All Raw Data
                      </span>
                      <span className="material-symbols-outlined text-slate-400 transition-transform group-open:rotate-180">
                        expand_more
                      </span>
                    </div>
                  </summary>
                  <div className="mt-4 border-t border-slate-100 dark:border-slate-800 pt-4">
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-4">
                      Raw AI extraction results with confidence scores. This shows what was
                      extracted from the document before validation against the system.
                    </p>
                    <div className="space-y-1">
                      {(() => {
                        // Define field ordering
                        const fieldOrder = [
                          // Shipment header
                          "pro_number",
                          "pickup_date",
                          "delivery_date",
                          "origin_sic_code",
                          "destination_sic_code",
                          "service_level",
                          "billing_terms",
                          // Shipping parties
                          "shipper",
                          "consignee",
                          "bill_to",
                          // Shipment details
                          "shipment_details",
                          "pieces",
                          "pallets",
                          "weight",
                          "weight_unit",
                          "cubage",
                          // Traces
                          "trace_numbers",
                          // Instructions and notes
                          "special_instructions",
                          "handwritten_notes",
                          "suggested_notes",
                          "suggested_instruction_codes",
                          "suggested_tags",
                          "appointment_required",
                        ];

                        // Get all entries and sort them
                        const entries = Object.entries(documentData.extractedData);
                        const sortedEntries = entries.sort(([keyA], [keyB]) => {
                          const indexA = fieldOrder.indexOf(keyA);
                          const indexB = fieldOrder.indexOf(keyB);

                          // If both are in the order list, sort by that order
                          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                          // If only A is in the list, it comes first
                          if (indexA !== -1) return -1;
                          // If only B is in the list, it comes first
                          if (indexB !== -1) return 1;
                          // Otherwise maintain original order
                          return 0;
                        });

                        return sortedEntries.map(([key, data]: [string, unknown]) => {
                          const fieldData = data as ExtractedFieldData & {
                            corrected?: boolean;
                            originalValue?: string;
                          };
                          const confidencePercent = fieldData.confidence * 100;
                          const isLowConfidence = confidencePercent < 90;
                          const isCorrected = fieldData.corrected;
                          const hasValidation = !!fieldData.validation;

                          // Determine background color based on validation status or confidence
                          let bgClass = "hover:bg-slate-100 dark:hover:bg-slate-800/50";
                          if (isCorrected) {
                            bgClass =
                              "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800";
                          } else if (hasValidation) {
                            const status = fieldData.validation?.status;
                            if (status === "auto_corrected") {
                              bgClass =
                                "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800";
                            } else if (
                              status === "suggested_add" ||
                              status === "suggested_update" ||
                              status === "suggested_review"
                            ) {
                              bgClass =
                                "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800";
                            } else if (status === "flagged") {
                              bgClass =
                                "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800";
                            } else if (status === "requires_review") {
                              bgClass =
                                "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800";
                            }
                          } else if (isLowConfidence) {
                            bgClass = getConfidenceColorHighlight(confidencePercent);
                          }

                          return (
                            <div key={key} className={`flex flex-col rounded-lg p-3 ${bgClass}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-sm font-medium ${
                                      isCorrected
                                        ? "text-blue-700 dark:text-blue-300 font-medium"
                                        : hasValidation &&
                                            fieldData.validation?.status === "requires_review"
                                          ? "text-red-700 dark:text-red-300"
                                          : hasValidation &&
                                              fieldData.validation?.status === "flagged"
                                            ? "text-yellow-700 dark:text-yellow-300"
                                            : hasValidation &&
                                                (fieldData.validation?.status === "suggested_add" ||
                                                  fieldData.validation?.status ===
                                                    "suggested_update" ||
                                                  fieldData.validation?.status ===
                                                    "suggested_review")
                                              ? "text-blue-700 dark:text-blue-300"
                                              : getConfidenceTextColor(confidencePercent)
                                    } flex items-center gap-1`}
                                  >
                                    {formatFieldName(key)}
                                    {isCorrected && (
                                      <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                                        (Corrected)
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Confidence indicator - show for all fields */}
                                  <span
                                    className="text-[10px] text-slate-500 dark:text-slate-400 font-medium"
                                    title={`${confidencePercent.toFixed(0)}% confidence`}
                                  >
                                    {confidencePercent.toFixed(0)}%
                                  </span>
                                  {isCorrected ? (
                                    <svg
                                      className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  ) : (
                                    !hasValidation &&
                                    isLowConfidence && (
                                      <span
                                        className={`flex size-2 rounded-full flex-shrink-0 ${getConfidenceColor(confidencePercent)}`}
                                        title={`${confidencePercent.toFixed(0)}% confidence`}
                                      />
                                    )
                                  )}
                                  {/* Validation Icon */}
                                  {hasValidation && fieldData.validation && (
                                    <ValidationIcon
                                      fieldName={formatFieldName(key)}
                                      validation={fieldData.validation}
                                      currentValue={formatValueForValidation(fieldData.value, key)}
                                      confidence={fieldData.confidence}
                                    />
                                  )}
                                </div>
                              </div>
                              {isCorrected && fieldData.originalValue && (
                                <span className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                  Original: <del>{fieldData.originalValue}</del>
                                </span>
                              )}
                              <div
                                className={`mt-2 text-sm ${
                                  isCorrected
                                    ? "text-blue-900 dark:text-blue-100"
                                    : getConfidenceValueColor(confidencePercent)
                                }`}
                              >
                                {fieldData.value !== null && fieldData.value !== undefined
                                  ? formatFieldValue(fieldData.value, key)
                                  : "N/A"}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </details>
              )}

            {/* Action Buttons */}
            <ReviewActions
              onFlag={handleFlagForReview}
              onConfirm={handleExportJSON}
              className="mt-8"
            />
          </div>
        </div>
      </main>
    </div>
  );
}
